import Hammer from 'hammerjs'

/**
 * Simple pinch zoom & panning for any element.
 * License: GPL3
 * 
 * Usage:
 * `new Zoomable($el, { option: value })`
 * 
 * Options:
 * - container : Container DOM element to bind gesture listeners to
 * - canZoom   : Callback function that should return boolean value, this 
 *               enables / disables zooming / panning at the time of the 
 *               gesture event.
 * 
 * API:
 * - reset()
 * - on('event.handle', ({zoomable}) => {})
 * 
 * Events:
 * - after.init
 * - after.pan
 * - after.transform
 * - after.reset
 * 
 * Derived from works contributed here:
 * https://stackoverflow.com/questions/18011099/pinch-to-zoom-using-hammer-js
 */

class Zoomable
{
    constructor(elm, opts = {}) {
        this.posX = 0,
        this.posY = 0,
        this.scale = 1,
        this.last_scale = 1,
        this.last_posX = 0,
        this.last_posY = 0,
        this.max_pos_x = 0,
        this.max_pos_y = 0,
        this.el = elm;
        this.opts = {
            canZoom: null,
            ...opts
        }
        this.container = this.opts.container ?? elm.parentNode
        this.eventHandlers = {}

        this.setupHammerJs()

        this.fire('after.init', { zoomable: this })
    }

    setupHammerJs() {
        this.hammertime = new Hammer(this.container, { touchAction: 'pan-x pan-y' });
        this.hammertime.get('pinch').set({
            enable: true
        });
        this.hammertime.get('pan').set({
            enable: () => this.scale !== 1
        })
        this.hammertime.on('doubletap pan pinch panend pinchend', (ev) => {
            this.handleHammerEvents(ev)
        });
    }

    canZoom() {
        return !this.opts.canZoom || 
            this.opts.canZoom === true || 
            typeof this.opts.canZoom === 'function' && this.opts.canZoom(this)
    }

    on(eventHandle, callback) {
        if (!this.eventHandlers[eventHandle]) {
            this.eventHandlers[eventHandle] = []
        }

        this.eventHandlers[eventHandle].push(callback)

        return this
    }

    fire(eventHandle, payload) {
        if (this.eventHandlers[eventHandle]) {
            this.eventHandlers[eventHandle].forEach(handler => {
                handler(payload)
            })
        }
    }

    handleHammerEvents(ev) {
        if (!this.canZoom()) {
            return
        }

        switch (ev.type)
        {
            case "doubletap":
                this.handleDoubleTap(ev);
                break;
            case "pan":
                this.handlePan(ev);
                break;
            case "panend":
                this.handlePanEnd(ev);
                break;
            case "pinch":
                this.scale = Math.max(1, Math.min(this.last_scale * (ev.scale), 4));
                break;
            case "pinchend":
                this.last_scale = this.scale;
                break;
        }

        this.transform();
    }

    handleDoubleTap(ev) {
        let transform

        if (this.scale !== 2) {
            transform =
                "translate3d(0, 0, 0) " +
                "scale3d(2, 2, 1) ";

            this.scale = 2;
            this.last_scale = 2;
        } else {
            this.scale = 1;
            this.last_scale = 1;
            transform = 'none'
        }

        this.el.style.webkitTransform = transform;
    }

    handlePan(ev) {
        if (this.scale != 1) {
            this.posX = this.last_posX + ev.deltaX;
            this.posY = this.last_posY + ev.deltaY;
            this.max_pos_x = Math.ceil((this.scale - 1) * this.el.clientWidth / 2);
            this.max_pos_y = Math.ceil((this.scale - 1) * this.el.clientHeight / 2);

            if (this.posX > this.max_pos_x) {
                this.posX = this.max_pos_x;
            }
            if (this.posX < -this.max_pos_x) {
                this.posX = -this.max_pos_x;
            }
            if (this.posY > this.max_pos_y) {
                this.posY = this.max_pos_y;
            }
            if (this.posY < -this.max_pos_y) {
                this.posY = -this.max_pos_y;
            }
        }
    }

    handlePanEnd(ev) {
        this.handlePan(ev)

        this.last_posX = this.posX < this.max_pos_x ? this.posX : this.max_pos_x;
        this.last_posY = this.posY < this.max_pos_y ? this.posY : this.max_pos_y;

        this.fire('after.pan', { zoomable: this })
    }

    transform() {
        let transform

        if (this.scale != 1) {
            this.hammertime.set({ touchAction: 'compute' })
            transform =
                "translate3d(" + this.posX + "px," + this.posY + "px, 0) " +
                "scale3d(" + this.scale + ", " + this.scale + ", 1)";
        } else {
            this.hammertime.set({ touchAction: 'pan-x pan-y' })
            transform = "none"
        }

        if (transform) {
            this.el.style.webkitTransform = transform;
        }

        this.fire('after.transform', { zoomable: this })
    }

    reset() {
        this.scale = 1
        this.transform()
        this.fire('after.reset', { zoomable: this })
    }
}

export default Zoomable